import { useEffect, useRef, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { getTournaments } from "@/lib/api";
import { scopedKeyOf } from "@/lib/queryClient";
import type { Match, Season } from "@/lib/types";
import { useTransferMatch } from "../hooks/useTransferMatch";
import {
  layoutLabel,
  matchHref,
  matchStatusLabel,
  recordingLabel,
} from "../matchLibrary";

export function LibraryDialog({
  matchId,
  title,
  description,
  onClose,
  busy = false,
  children,
  testId,
  width = "standard",
}: {
  matchId: string;
  title: string;
  description?: string;
  onClose: () => void;
  busy?: boolean;
  children: ReactNode;
  testId: string;
  width?: "standard" | "wide" | "recordings";
}) {
  const previous = useRef(
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="admin-library-dialog-overlay" />
        <Dialog.Content
          className="admin-library-dialog"
          data-match-id={matchId}
          data-dialog-width={width}
          data-testid={testId}
          onEscapeKeyDown={(event) => {
            if (busy) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (
              busy ||
              (event.target instanceof Element &&
                event.target.closest('[data-tktic-popover="select"]'))
            )
              event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const fallback = document.querySelector<HTMLElement>(
              `[data-match-id="${CSS.escape(matchId)}"] [data-testid="admin-matches-row-details"]`,
            );
            (previous.current?.isConnected
              ? previous.current
              : fallback
            )?.focus();
          }}
        >
          <header>
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label={`Close ${title.toLowerCase()}`}
                disabled={busy}
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </header>
          <Dialog.Description
            className={
              description ? "admin-library-dialog-description" : "sr-only"
            }
          >
            {description ?? title}
          </Dialog.Description>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function MatchDetailsDialog({
  match,
  teamName,
  seasonName,
  onClose,
  onMedia,
  onEdit,
}: {
  match: Match;
  teamName: string;
  seasonName: string;
  onClose: () => void;
  onMedia: () => void;
  onEdit: () => void;
}) {
  const fields = [
    ["Date & kick-off", `${match.date}${match.time ? ` · ${match.time}` : ""}`],
    ["Season", seasonName],
    [
      "Competition & round",
      [
        match.tournament_name ||
          (match.competition === "friendly"
            ? "Friendly"
            : match.competition === "tournament"
              ? "Tournament"
              : "League"),
        match.stage,
      ]
        .filter(Boolean)
        .join(" · "),
    ],
    ["Location", match.location || "Not recorded"],
    [
      "Score",
      match.score_home == null || match.score_away == null
        ? "—"
        : `${match.score_home}–${match.score_away}`,
    ],
    ["Recording", `${recordingLabel(match)} · ${layoutLabel(match)}`],
  ];
  return (
    <LibraryDialog
      matchId={match.id}
      width="wide"
      title="Match details"
      onClose={onClose}
      testId="admin-match-details"
    >
      <div className="admin-library-dialog-body">
        <div className="admin-library-details-heading">
          <div>
            <p className="eyebrow">{teamName}</p>
            <h3>
              {match.home_team} vs {match.away_team}
            </h3>
          </div>
          <span className="admin-library-status">
            {matchStatusLabel(match)}
          </span>
        </div>
        <dl className="admin-library-details-grid">
          {fields.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
          {match.pk_home != null && match.pk_away != null ? (
            <div>
              <dt>Penalty shootout</dt>
              <dd>
                {match.pk_home}–{match.pk_away}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
      <footer>
        <button type="button" onClick={onMedia}>
          Media slots
        </button>
        <button type="button" onClick={onEdit}>
          Edit match
        </button>
        <a
          className="admin-library-primary"
          href={matchHref(match)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open match
        </a>
        <button type="button" onClick={onClose}>
          Done
        </button>
      </footer>
    </LibraryDialog>
  );
}

export function TransferMatchDialog({
  match,
  teams,
  seasons,
  onClose,
}: {
  match: Match;
  teams: { id: string; name: string }[];
  seasons: Season[];
  onClose: () => void;
}) {
  const mustResolveTournament = !!(
    match.tournament_id || match.tournament_name
  );
  const schema = z
    .object({
      destination_team_id: z.string().min(1, "Choose a destination squad."),
      destination_season_id: z.string().min(1, "Choose a season."),
      team_side: z.enum(["home", "away", "none"], {
        error: "Choose which side the destination squad plays.",
      }),
      tournament_id: z.string().nullable(),
      clear_tournament_ack: z.boolean(),
    })
    .superRefine((value, ctx) => {
      if (
        mustResolveTournament &&
        !value.tournament_id &&
        !value.clear_tournament_ack
      )
        ctx.addIssue({
          code: "custom",
          path: ["clear_tournament_ack"],
          message:
            "Confirm clearing the old tournament, or select a destination tournament.",
        });
    });
  type Values = z.infer<typeof schema>;
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      destination_team_id: "",
      destination_season_id: match.season_id ?? "",
      tournament_id: null,
      clear_tournament_ack: false,
      team_side:
        match.home_team_id === match.team_id
          ? "home"
          : match.away_team_id === match.team_id
            ? "away"
            : undefined,
    },
  });
  const destination = form.watch("destination_team_id");
  const season = form.watch("destination_season_id");
  const selectedTournament = form.watch("tournament_id");
  const tournaments = useQuery({
    queryKey: scopedKeyOf(
      { teamId: destination || null, seasonId: season || null },
      "tournaments",
    ),
    queryFn: () => getTournaments(destination, season),
    enabled: !!destination && !!season && match.competition === "tournament",
  });
  const { setValue } = form;
  useEffect(() => {
    setValue("tournament_id", null);
    setValue("clear_tournament_ack", false);
  }, [destination, season, setValue]);
  const mutation = useTransferMatch();
  const submit = form.handleSubmit(async (values) => {
    if (
      values.tournament_id &&
      !tournaments.data?.tournaments.some((t) => t.id === values.tournament_id)
    ) {
      form.setError("tournament_id", {
        message: "Choose an available destination tournament.",
      });
      return;
    }
    const { clear_tournament_ack: _ack, ...body } = values;
    try {
      await mutation.mutateAsync({
        id: match.id,
        sourceTeamId: match.team_id ?? "",
        body: { ...body, expected_updated_at: match.updated_at ?? "" },
      });
      onClose();
    } catch {
      /* mutation renders the server's safe error */
    }
  });
  return (
    <LibraryDialog
      matchId={match.id}
      title="Move match to squad"
      description={`${match.home_team} vs ${match.away_team}`}
      onClose={onClose}
      busy={mutation.isPending}
      testId="admin-move-match-dialog"
    >
      <form onSubmit={submit}>
        <fieldset
          className="admin-library-dialog-body"
          disabled={mutation.isPending}
        >
          <div className="admin-library-details-grid">
            <label>
              <span>Destination squad</span>
              <Controller
                control={form.control}
                name="destination_team_id"
                render={({ field }) => (
                  <TkticSelect
                    value={field.value}
                    onChange={field.onChange}
                    ariaLabel="Destination squad"
                    options={[
                      { value: "", label: "Choose a squad" },
                      ...teams
                        .filter((t) => t.id !== match.team_id)
                        .map((t) => ({ value: t.id, label: t.name })),
                    ]}
                  />
                )}
              />
            </label>
            <label>
              <span>Destination season</span>
              <Controller
                control={form.control}
                name="destination_season_id"
                render={({ field }) => (
                  <TkticSelect
                    value={field.value}
                    onChange={field.onChange}
                    ariaLabel="Destination season"
                    options={[
                      { value: "", label: "Choose a season" },
                      ...seasons.map((s) => ({ value: s.id, label: s.name })),
                    ]}
                  />
                )}
              />
            </label>
          </div>
          <fieldset className="admin-library-side">
            <legend>Destination squad plays</legend>
            <Controller
              control={form.control}
              name="team_side"
              render={({ field }) => (
                <div role="radiogroup" aria-label="Destination squad plays">
                  {(["home", "away", "none"] as const).map((side) => (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={field.value === side}
                      tabIndex={
                        field.value === side ||
                        (!field.value && side === "home")
                          ? 0
                          : -1
                      }
                      key={side}
                      onKeyDown={(event) => {
                        const sides = ["home", "away", "none"] as const;
                        const index = sides.indexOf(side);
                        const next =
                          event.key === "Home"
                            ? 0
                            : event.key === "End"
                              ? 2
                              : ["ArrowRight", "ArrowDown"].includes(event.key)
                                ? (index + 1) % 3
                                : ["ArrowLeft", "ArrowUp"].includes(event.key)
                                  ? (index + 2) % 3
                                  : null;
                        if (next !== null) {
                          event.preventDefault();
                          field.onChange(sides[next]);
                          event.currentTarget.parentElement
                            ?.querySelectorAll<HTMLButtonElement>("button")
                            [next]?.focus();
                        }
                      }}
                      onClick={() => field.onChange(side)}
                    >
                      {side === "none"
                        ? "Neither side"
                        : side === "home"
                          ? "Home"
                          : "Away"}
                    </button>
                  ))}
                </div>
              )}
            />
          </fieldset>
          {match.competition === "tournament" ? (
            <label className="admin-library-tournament">
              <span>Destination tournament</span>
              <Controller
                control={form.control}
                name="tournament_id"
                render={({ field }) => (
                  <TkticSelect
                    value={field.value ?? "__clear__"}
                    onChange={(value) =>
                      field.onChange(value === "__clear__" ? null : value)
                    }
                    ariaLabel="Destination tournament"
                    disabled={!destination || !season || tournaments.isFetching}
                    options={[
                      {
                        value: "__clear__",
                        label: "Clear tournament assignment",
                      },
                      ...(tournaments.data?.tournaments ?? []).map((t) => ({
                        value: t.id,
                        label: t.name,
                      })),
                    ]}
                  />
                )}
              />
              {tournaments.isError ? (
                <small role="alert">
                  Could not load destination tournaments. Try again or clear the
                  assignment.
                </small>
              ) : null}
            </label>
          ) : null}
          {mustResolveTournament && !selectedTournament ? (
            <Controller
              control={form.control}
              name="clear_tournament_ack"
              render={({ field }) => (
                <button
                  className="admin-library-clear-ack"
                  type="button"
                  role="checkbox"
                  aria-checked={field.value}
                  onClick={() => field.onChange(!field.value)}
                >
                  {field.value ? <Check size={16} aria-hidden="true" /> : null}
                  Clear the original tournament, finish and stage.
                </button>
              )}
            />
          ) : null}
          <p className="admin-library-transfer-review">
            {teams.find((t) => t.id === match.team_id)?.name ?? "Current squad"}{" "}
            →{" "}
            {teams.find((t) => t.id === destination)?.name ??
              "Choose destination squad"}
          </p>
          <p>
            Recordings stay attached. Access follows the destination squad.
            Matches with linked coaching feedback cannot be moved.
          </p>
          {Object.entries(form.formState.errors).map(([key, error]) => (
            <p role="alert" className="text-destructive" key={key}>
              {error.message}
            </p>
          ))}
          {mutation.error ? (
            <p role="alert" className="text-destructive">
              {typeof mutation.error.detail === "string"
                ? mutation.error.detail
                : "Could not move this match. Reload and try again."}
            </p>
          ) : null}
        </fieldset>
        <footer>
          <button type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            className="admin-library-primary"
            disabled={
              mutation.isPending ||
              (!!selectedTournament && tournaments.isFetching)
            }
          >
            {mutation.isPending ? "Moving…" : "Move match"}
          </button>
        </footer>
      </form>
    </LibraryDialog>
  );
}
